import { useEffect, useId, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type LoginResult } from '../lib/api'
import { isClaudeManualLoginUrl } from '../lib/claude-login-url'

type CtaState = 'idle' | 'confirm' | 'running' | 'done' | 'failed'

/** The manual fallback shown on failure (and copyable any time) — what a member can run themselves
 *  from a terminal if the assisted flow can't complete (e.g. no default browser configured). */
const CLAUDE_LOGIN_CMD = 'claude auth login'

/** Tail kept on screen even though the server already caps the captured log at 8000 chars — a
 *  failed sign-in's useful line (the actual error, or the URL to open by hand) is almost always
 *  at the END. */
const LOG_TAIL_CHARS = 2000

/**
 * The Claude-only assisted sign-in affordance — shown on AiAgentSection's Claude card when
 * `claude auth status` says the CLI isn't logged in, and inside the chat panel (banner, or on the
 * message that just failed) when a run reports `agent_auth_required`.
 *
 * Same state machine as ClaudeInstallCta, one step of consent ahead of anything running on the
 * member's machine: idle ("Sign in") → confirm (what this does) → running → done | failed.
 *
 * ## Why the running state carries a link and a text field
 *
 * `claude auth login --claudeai` opens a browser itself — except that from inside Niobe's hidden
 * child process on Windows, its `rundll32` open silently does nothing. All the member used to see
 * was the CLI's own stdout as plain text: a URL whose page hands back a `code#state`, and nowhere
 * in Niobe to put it. That is the dead end this component now closes, on every platform:
 *
 * - **"Open the sign-in page"** — built from the `login-url` progress event, and only when
 *   {@link isClaudeManualLoginUrl} accepts it. `target="_blank"` matters in Desktop: the window's
 *   `setWindowOpenHandler` routes that to the system browser, whereas a plain `href` would
 *   navigate the Electron window away from the app.
 * - **The code field** — `POST /agent/login/:jobId/code` writes it to the CLI's stdin, which it
 *   reads even when stdin is a pipe. A 400 means the CLI is still waiting and the member can
 *   retype; a 404/409 means that attempt is over and "Try again" starts a fresh one.
 *
 * On a packaged Windows build this usually never comes up: the shell points `BROWSER` at itself so
 * the CLI's *automatic* (no-code) URL opens properly. When that fails, this is what it degrades to.
 */
export function ClaudeLoginCta({ onSignedIn, label }: { onSignedIn: () => void; label?: string }) {
  const { t } = useLang()
  const [state, setState] = useState<CtaState>('idle')
  const [jobId, setJobId] = useState<string | null>(null)
  /** Only set when triggerLogin() itself throws (no job was ever created to poll for a result). */
  const [triggerError, setTriggerError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  /** A rejected code the CLI is still waiting on — shown next to the field, not as a failure. */
  const [codeError, setCodeError] = useState<string | null>(null)
  const logId = useId()
  const codeId = useId()
  const codeErrorId = useId()

  const jobQuery = useQuery({
    queryKey: ['agent-job', jobId],
    queryFn: () => api.agentJob(jobId as string),
    enabled: state === 'running' && !!jobId,
    refetchInterval: 2000,
  })
  const job = jobQuery.data
  const result = job?.result as LoginResult | undefined

  useEffect(() => {
    if (state !== 'running' || !job) return
    if (job.status === 'cancelled') {
      // The member asked to stop — back to idle, not an error state.
      setState('idle')
      setJobId(null)
    } else if (job.status === 'done') {
      // 'done' only means loginClaude() resolved WITHOUT throwing — it still needs to be asked
      // whether the sign-in itself actually worked (the CLI's own `auth status` confirmed
      // loggedIn), same "job status isn't the verdict" care as ClaudeInstallCta.
      if (result?.ok) {
        setState('done')
        onSignedIn()
      } else {
        setState('failed')
      }
    } else if (job.status === 'error') {
      setState('failed')
    }
  }, [state, job, result, onSignedIn])

  async function confirmLogin(): Promise<void> {
    setTriggerError(null)
    setCode('')
    setCodeError(null)
    try {
      const { jobId: id } = await api.triggerLogin('claude')
      setJobId(id)
      setState('running')
    } catch (e) {
      setTriggerError(e instanceof Error ? e.message : String(e))
      setState('failed')
    }
  }

  async function cancelLogin(): Promise<void> {
    if (jobId) await api.cancelJob(jobId)
  }

  /**
   * Hand the pasted code to the CLI. A 400 is NOT a failure — the CLI is still sitting on its
   * prompt, so the member gets an inline hint and another go. Anything else (404: the job is gone;
   * 409: the CLI already ended this attempt) means only a fresh sign-in can help.
   *
   * On success we deliberately do not declare victory: the job keeps polling, and the CLI's own
   * verdict decides between 'done' and 'failed'.
   */
  async function submitCode(): Promise<void> {
    const trimmed = code.trim()
    if (!jobId || !trimmed || submitting) return
    setSubmitting(true)
    setCodeError(null)
    try {
      await api.submitLoginCode(jobId, trimmed)
      setCode('')
    } catch (e) {
      const status = (e as { status?: number }).status
      if (status === 400) setCodeError(t('claude_login_code_invalid'))
      else setState('failed')
    } finally {
      setSubmitting(false)
    }
  }

  async function copyCommand(): Promise<void> {
    try {
      await navigator.clipboard.writeText(CLAUDE_LOGIN_CMD)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      /* clipboard unavailable — the command text is still selectable on screen */
    }
  }

  if (state === 'idle') {
    // `label` exists for the proactive expiry warning, where the member IS still signed in and
    // "Sign in" would read as though Niobe had lost track: there it says "Renew now". Only the
    // resting button's wording changes — the flow behind it is identical, and a new sign-in really
    // is the only thing that resets the hard login expiry.
    return (
      <button className="mini ok" onClick={() => setState('confirm')}>
        {label ?? t('claude_login_cta')}
      </button>
    )
  }

  if (state === 'confirm') {
    return (
      <div className="claude-login" role="status" aria-live="polite">
        <p className="claude-login-title">
          <b>{t('claude_login_confirm_title')}</b>
        </p>
        <p>{t('claude_login_confirm_body')}</p>
        <div className="claude-login-actions">
          <button className="mini ok" onClick={() => void confirmLogin()}>
            {t('claude_login_confirm_yes')}
          </button>
          <button className="mini ghost" onClick={() => setState('idle')}>
            {t('claude_install_confirm_cancel')}
          </button>
        </div>
      </div>
    )
  }

  if (state === 'running') {
    // The manual sign-in URL arrives on the job's progress channel under its own `login-url` tool
    // (the passerelle already allowlisted it); re-checked here because this component, not the
    // passerelle, is the one turning it into something clickable.
    const manualUrl = [...(job?.progress ?? [])]
      .reverse()
      .map((p) => (p.tool === 'login-url' ? p.target : undefined))
      .find((target): target is string => isClaudeManualLoginUrl(target))
    return (
      <div className="claude-login" role="status" aria-live="polite">
        <span className="thinking">
          <span className="dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          {t('claude_login_running')}
        </span>
        <p className="claude-login-fallback-label">{t('claude_login_no_browser')}</p>
        {manualUrl && (
          <a className="claude-login-link" href={manualUrl} target="_blank" rel="noopener noreferrer">
            {t('claude_login_open_page')}
          </a>
        )}
        <label className="claude-login-code-label" htmlFor={codeId}>
          {t('claude_login_code_label')}
        </label>
        <div className="claude-login-actions">
          <input
            id={codeId}
            className="mono claude-login-code"
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={code}
            disabled={submitting}
            {...(codeError ? { 'aria-describedby': codeErrorId, 'aria-invalid': true } : {})}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void submitCode()
              }
            }}
          />
          <button className="mini ok" disabled={submitting || code.trim() === ''} onClick={() => void submitCode()}>
            {submitting ? t('claude_login_code_submitting') : t('claude_login_code_submit')}
          </button>
        </div>
        {codeError && (
          <p id={codeErrorId} className="claude-login-code-error">
            {codeError}
          </p>
        )}
        <div className="claude-login-actions">
          <button className="mini ghost" onClick={() => void cancelLogin()}>
            {t('stop_generating')}
          </button>
        </div>
      </div>
    )
  }

  if (state === 'done') {
    return (
      <div className="claude-login" role="status" aria-live="polite">
        <p className="claude-login-success">
          {result?.email ? t('claude_login_done_account').replace('{email}', () => result.email as string) : t('claude_login_done')}
        </p>
      </div>
    )
  }

  // state === 'failed'
  const log = (result?.log ?? triggerError ?? '').slice(-LOG_TAIL_CHARS)
  return (
    <div className="claude-login" role="status" aria-live="polite">
      <p className="claude-login-failed-title">
        <b>{t('claude_login_failed')}</b>
      </p>
      {log !== '' && (
        <>
          <p id={logId} className="claude-login-log-label">
            {t('claude_install_log_label')}
          </p>
          <pre className="mono claude-login-log" tabIndex={0} aria-labelledby={logId}>
            {log}
          </pre>
        </>
      )}
      {/* A wrong code ends the CLI's attempt outright — there is no retrying INTO it, only a fresh
          sign-in. Hence a real restart here rather than "paste it again". */}
      <div className="claude-login-actions">
        <button className="mini ok" onClick={() => void confirmLogin()}>
          {t('claude_login_retry')}
        </button>
      </div>
      <p>{t('claude_login_manual_label')}</p>
      <div className="claude-login-actions">
        <code className="mono claude-login-cmd">{CLAUDE_LOGIN_CMD}</code>
        <button className="mini ok" onClick={() => void copyCommand()}>
          {copied ? t('ext_copied') : t('ext_copy')}
        </button>
      </div>
    </div>
  )
}
